import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { 
  Calendar, 
  Users, 
  MessageSquare, 
  BarChart3,
  LogOut,
  Check,
  X,
  Clock,
  Eye,
  Trash2,
  RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: { bg: "bg-yellow-500/20", text: "text-yellow-400", label: "Pending" },
  confirmed: { bg: "bg-green-500/20", text: "text-green-400", label: "Confirmed" },
  cancelled: { bg: "bg-red-500/20", text: "text-red-400", label: "Cancelled" },
  completed: { bg: "bg-blue-500/20", text: "text-blue-400", label: "Completed" },
  new: { bg: "bg-cyan-500/20", text: "text-cyan-400", label: "New" },
  read: { bg: "bg-gray-500/20", text: "text-gray-400", label: "Read" },
  resolved: { bg: "bg-green-500/20", text: "text-green-400", label: "Resolved" },
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders, isAuthenticated, isAdmin, loading } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [patients, setPatients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!loading && (!isAuthenticated || !isAdmin)) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        const headers = getAuthHeaders();
        const [statsRes, appointmentsRes, contactsRes, usersRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, { headers }),
          axios.get(`${API}/admin/appointments`, { headers }),
          axios.get(`${API}/admin/contacts`, { headers }),
          axios.get(`${API}/admin/users`, { headers }),
        ]);
        setStats(statsRes.data);
        setAppointments(appointmentsRes.data);
        setContacts(contactsRes.data);
        setPatients(usersRes.data);
      } catch (error) {
        console.error("Error fetching admin data:", error);
        toast.error("Failed to load dashboard data");
      } finally {
        setIsLoading(false);
      }
    };

    if (isAuthenticated && isAdmin) {
      fetchData();
    }
  }, [isAuthenticated, isAdmin, loading, navigate, getAuthHeaders]);

  const handleLogout = () => {
    logout();
    toast.success("Logged out successfully");
    navigate('/');
  };

  const updateAppointmentStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/appointments/${id}`, { status }, {
        headers: getAuthHeaders()
      });
      setAppointments(prev => prev.map(apt => 
        apt.id === id ? { ...apt, status } : apt
      ));
      toast.success("Appointment updated");
    } catch (error) {
      console.error("Error updating appointment:", error);
      toast.error("Failed to update appointment");
    }
  };

  const deleteAppointment = async (id) => {
    if (!window.confirm("Are you sure you want to delete this appointment?")) return;
    
    try {
      await axios.delete(`${API}/admin/appointments/${id}`, {
        headers: getAuthHeaders()
      });
      setAppointments(prev => prev.filter(apt => apt.id !== id));
      toast.success("Appointment deleted");
    } catch (error) {
      console.error("Error deleting appointment:", error);
      toast.error("Failed to delete appointment");
    }
  };

  const updateContactStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/contacts/${id}?status=${status}`, {}, {
        headers: getAuthHeaders()
      });
      setContacts(prev => prev.map(c => 
        c.id === id ? { ...c, status } : c
      ));
      toast.success("Contact updated");
    } catch (error) {
      console.error("Error updating contact:", error);
      toast.error("Failed to update contact");
    }
  };

  if (loading || isLoading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-[#00f0ff] text-xl">Loading...</div>
      </div>
    );
  }

  const tabs = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "appointments", label: "Appointments", icon: Calendar },
    { id: "contacts", label: "Messages", icon: MessageSquare },
    { id: "patients", label: "Patients", icon: Users },
  ];

  return (
    <div data-testid="admin-dashboard" className="pt-20 min-h-screen bg-[#0f0518]">
      {/* Header */}
      <section className="py-6 border-b border-[#4b0082]/30 bg-[#1a0b2e]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
              <p className="text-gray-400 text-sm">Manage appointments, contacts, and patients</p>
            </div>
            <Button 
              onClick={handleLogout}
              data-testid="admin-logout"
              variant="outline"
              className="border-[#4b0082] text-gray-300 rounded-full hover:border-[#ff00ff] hover:text-[#ff00ff]"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="border-b border-[#4b0082]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'text-[#00f0ff] border-[#00f0ff]'
                    : 'text-gray-400 border-transparent hover:text-white'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Overview Tab */}
          {activeTab === "overview" && stats && (
            <div className="space-y-8">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: "Total Appointments", value: stats.total_appointments, color: "#00f0ff", icon: Calendar },
                  { label: "Pending", value: stats.pending_appointments, color: "#ff9e00", icon: Clock },
                  { label: "Messages", value: stats.total_contacts, color: "#ff00ff", icon: MessageSquare },
                  { label: "Patients", value: stats.total_patients, color: "#00f0ff", icon: Users },
                ].map((stat, index) => (
                  <div key={index} className="glass rounded-xl p-5">
                    <div className="flex items-center justify-between mb-3">
                      <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                    </div>
                    <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                    <div className="text-gray-400 text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="grid lg:grid-cols-2 gap-8">
                {/* Recent Appointments */}
                <div className="glass rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Recent Appointments</h3>
                  <div className="space-y-3">
                    {appointments.slice(0, 5).map((apt) => {
                      const status = statusColors[apt.status] || statusColors.pending;
                      return (
                        <div key={apt.id} className="flex items-center justify-between p-3 bg-[#0f0518] rounded-lg">
                          <div>
                            <p className="text-white text-sm font-medium">{apt.first_name} {apt.last_name}</p>
                            <p className="text-gray-400 text-xs">{apt.service} - {apt.preferred_date}</p>
                          </div>
                          <Badge className={`${status.bg} ${status.text} border-0`}>
                            {status.label}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Recent Messages */}
                <div className="glass rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Recent Messages</h3>
                  <div className="space-y-3">
                    {contacts.slice(0, 5).map((contact) => {
                      const status = statusColors[contact.status] || statusColors.new;
                      return (
                        <div key={contact.id} className="flex items-center justify-between p-3 bg-[#0f0518] rounded-lg">
                          <div>
                            <p className="text-white text-sm font-medium">{contact.name}</p>
                            <p className="text-gray-400 text-xs">{contact.subject}</p>
                          </div>
                          <Badge className={`${status.bg} ${status.text} border-0`}>
                            {status.label}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Appointments Tab */}
          {activeTab === "appointments" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white">All Appointments</h2>
                <span className="text-gray-400">{appointments.length} total</span>
              </div>
              
              {appointments.map((apt) => {
                const status = statusColors[apt.status] || statusColors.pending;
                return (
                  <div 
                    key={apt.id}
                    data-testid={`admin-apt-${apt.id}`}
                    className="glass rounded-xl p-5"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="text-white font-semibold">{apt.first_name} {apt.last_name}</h4>
                          <Badge className={`${status.bg} ${status.text} border-0`}>
                            {status.label}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-400">
                          <span>Service: {apt.service}</span>
                          <span>Date: {apt.preferred_date}</span>
                          <span>Time: {apt.preferred_time}</span>
                          <span>Email: {apt.email}</span>
                        </div>
                        {apt.reason && (
                          <p className="text-gray-500 text-sm mt-2">Reason: {apt.reason}</p>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Select 
                          value={apt.status}
                          onValueChange={(value) => updateAppointmentStatus(apt.id, value)}
                        >
                          <SelectTrigger className="w-32 bg-[#2a1b3d] border-[#4b0082] text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1a0b2e] border-[#4b0082]">
                            <SelectItem value="pending" className="text-white">Pending</SelectItem>
                            <SelectItem value="confirmed" className="text-white">Confirmed</SelectItem>
                            <SelectItem value="completed" className="text-white">Completed</SelectItem>
                            <SelectItem value="cancelled" className="text-white">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => deleteAppointment(apt.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Contacts Tab */}
          {activeTab === "contacts" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white">Contact Messages</h2>
                <span className="text-gray-400">{contacts.length} total</span>
              </div>
              
              {contacts.map((contact) => {
                const status = statusColors[contact.status] || statusColors.new;
                return (
                  <div 
                    key={contact.id}
                    data-testid={`admin-contact-${contact.id}`}
                    className="glass rounded-xl p-5"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="text-white font-semibold">{contact.name}</h4>
                          <Badge className={`${status.bg} ${status.text} border-0`}>
                            {status.label}
                          </Badge>
                        </div>
                        <p className="text-[#00f0ff] text-sm mb-1">{contact.subject}</p>
                        <p className="text-gray-400 text-sm">{contact.message}</p>
                        <div className="flex gap-4 mt-2 text-xs text-gray-500">
                          <span>{contact.email}</span>
                          {contact.phone && <span>{contact.phone}</span>}
                        </div>
                      </div>
                      
                      <Select 
                        value={contact.status}
                        onValueChange={(value) => updateContactStatus(contact.id, value)}
                      >
                        <SelectTrigger className="w-32 bg-[#2a1b3d] border-[#4b0082] text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a0b2e] border-[#4b0082]">
                          <SelectItem value="new" className="text-white">New</SelectItem>
                          <SelectItem value="read" className="text-white">Read</SelectItem>
                          <SelectItem value="resolved" className="text-white">Resolved</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Patients Tab */}
          {activeTab === "patients" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white">Registered Patients</h2>
                <span className="text-gray-400">{patients.length} total</span>
              </div>
              
              <div className="glass rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#1a0b2e]">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Phone</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">DOB</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#4b0082]/30">
                    {patients.map((patient) => (
                      <tr key={patient.id} data-testid={`patient-${patient.id}`}>
                        <td className="px-6 py-4 text-white">{patient.first_name} {patient.last_name}</td>
                        <td className="px-6 py-4 text-gray-400">{patient.email}</td>
                        <td className="px-6 py-4 text-gray-400">{patient.phone}</td>
                        <td className="px-6 py-4 text-gray-400">{patient.date_of_birth || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
